// Mobile Home: the workspace header, search, quick cards (Today, Replies, Assigned, Activity,
// Upcoming), then Recents, Channels, Direct messages, Favorites and Spaces.
import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Activity, AtSign, CalendarClock, CalendarDays, ChevronDown, ChevronRight, Hash, Layers, Lock, MessageSquareReply, Plus, Search, UserPlus } from '@/components/ui/icons';
import type { Favorite } from '@shared/types';
import { useDb, useMe, type Tables } from '@/lib/store';
import { useOpenTask, useWorkspaceId, locationPath } from '@/lib/nav';
import { spacesOf } from '@/lib/hierarchy';
import { toggleExpanded, useUi } from '@/lib/ui';
import { useIsGuest } from '@/lib/roles';
import { Avatar } from '@/components/ui/Avatar';
import { WorkspaceLogo, SpaceAvatar } from '@/shell/WorkspaceLogo';
import { channelTitle } from '@/shell/ChannelSections';
import { useActivityUnread } from '@/pages/chat/activity';
import { resolveRecent, type RecentRow } from '@/pages/home/RecentsCard';
import { isClosed, workGroup } from '@/pages/home/taskUtils';
import { openSheet } from './sheets';

/** Header shared by Home and Inbox: title (or workspace switcher), invite and your avatar. */
export function TopHeader({ title }: { title?: string }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const name = useDb((d) => d.workspaces[ws]?.name ?? 'Workspace');
  const guest = useIsGuest();
  return (
    <header className="m-top">
      {title ? (
        <h1 className="m-top-title">{title}</h1>
      ) : (
        <button type="button" className="m-ws" onClick={() => openSheet('workspaces')}>
          <WorkspaceLogo workspaceId={ws} size={34} />
          <span className="m-ws-name truncate">{name}</span>
          <ChevronDown size={16} />
        </button>
      )}
      <span className="spacer" />
      {!guest && (
        <button type="button" className="m-icon-btn" onClick={() => openSheet('invite')} aria-label="Invite people">
          <UserPlus size={21} />
        </button>
      )}
      <button type="button" className="m-me" onClick={() => openSheet('profile')} aria-label="Your profile">
        <Avatar userId={me} size={34} showPresence />
      </button>
    </header>
  );
}

export function SearchBar({ placeholder = 'Search' }: { placeholder?: string }) {
  return (
    <button type="button" className="m-search" onClick={() => useUi.setState({ commandOpen: true })}>
      <Search size={18} />
      <span>{placeholder}</span>
    </button>
  );
}

function Card({ icon, title, sub, onClick, accent }: { icon: ReactNode; title: ReactNode; sub: ReactNode; onClick: () => void; accent?: boolean }) {
  return (
    <button type="button" className={clsx('m-card', { accent })} onClick={onClick}>
      <span className="m-card-icon">{icon}</span>
      <span className="m-card-title">{title}</span>
      <span className="m-card-sub">{sub}</span>
    </button>
  );
}

/** A collapsible Home section with an optional + action. */
function Section({ id, title, onAdd, children }: { id: string; title: string; onAdd?: () => void; children: ReactNode }) {
  const open = useUi((s) => s.expanded[`m:${id}`] ?? true);
  return (
    <section className="m-home-section">
      <div className="m-home-section-head">
        <button type="button" className="m-home-section-title" onClick={() => toggleExpanded(`m:${id}`, !open)}>
          {title}
        </button>
        <span className="spacer" />
        {onAdd && (
          <button type="button" className="m-icon-btn sm" onClick={onAdd} aria-label={`New in ${title}`}>
            <Plus size={20} />
          </button>
        )}
        <button type="button" className="m-icon-btn sm" onClick={() => toggleExpanded(`m:${id}`, !open)} aria-label={open ? `Collapse ${title}` : `Expand ${title}`}>
          {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
      </div>
      {open && <div className="m-home-section-body">{children}</div>}
    </section>
  );
}

export function NavRow({ icon, label, extra, badge, onClick, chevron }: { icon: ReactNode; label: ReactNode; extra?: ReactNode; badge?: number; onClick: () => void; chevron?: boolean }) {
  return (
    <button type="button" className={clsx('m-nav-row', { unread: !!badge })} onClick={onClick}>
      <span className="m-nav-icon">{icon}</span>
      <span className="m-nav-label truncate">{label}</span>
      {extra}
      <span className="spacer" />
      {!!badge && <span className="m-badge">{badge > 99 ? '99+' : badge}</span>}
      {chevron && <ChevronRight size={18} className="faint" />}
    </button>
  );
}

function favTarget(db: Tables, f: Favorite, ws: string) {
  const t = f.target;
  if (t.type === 'space') return db.spaces[t.id]?.workspaceId === ws ? { name: db.spaces[t.id].name, path: locationPath(ws, t as never) } : null;
  if (t.type === 'folder') return db.folders[t.id] && db.spaces[db.folders[t.id].spaceId]?.workspaceId === ws ? { name: db.folders[t.id].name, path: locationPath(ws, t as never) } : null;
  if (t.type === 'list') return db.lists[t.id] && db.spaces[db.lists[t.id].spaceId]?.workspaceId === ws ? { name: db.lists[t.id].name, path: locationPath(ws, t as never) } : null;
  if (t.type === 'doc') return db.docs[t.id]?.workspaceId === ws ? { name: db.docs[t.id].title || 'Untitled', path: `/${ws}/docs/${t.id}` } : null;
  if (t.type === 'channel') return db.channels[t.id]?.workspaceId === ws ? { name: db.channels[t.id].name || 'Direct message', path: `/${ws}/chat/${t.id}` } : null;
  return null;
}

export function MobileHome() {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const openTask = useOpenTask();
  const guest = useIsGuest();
  const db = useDb((d) => d);
  const recents = useUi((s) => s.recents);
  const activity = useActivityUnread();

  const mine = useMemo(() => Object.values(db.tasks).filter((t) => t.workspaceId === ws && !t.archived && t.assigneeIds.includes(me) && !isClosed(db, t)), [db, ws, me]);
  const today = mine.filter((t) => workGroup(t) === 'today').length;
  const overdue = mine.filter((t) => workGroup(t) === 'overdue').length;
  const upcoming = mine.filter((t) => workGroup(t) === 'next').length;
  const replies = useMemo(() => Object.values(db.notifications).filter((n) => n.userId === me && n.kind === 'reply' && !n.readAt).length, [db, me]);
  const assigned = useMemo(() => Object.values(db.comments).filter((c) => c.assigneeId === me && !c.resolved && db.tasks[c.taskId]?.workspaceId === ws).length, [db, me, ws]);

  const recentRows = useMemo(
    () =>
      recents
        .map((r) => resolveRecent(db, r, ws, me, navigate, openTask))
        .filter((r): r is RecentRow => !!r)
        .slice(0, 5),
    [recents, db, ws, me, navigate, openTask],
  );
  const lastRead = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of Object.values(db.channelReads)) if (r.userId === me) out[r.channelId] = r.lastReadAt;
    return out;
  }, [db.channelReads, me]);
  const unreadIn = (id: string) => Object.values(db.messages).filter((m) => m.channelId === id && !m.parentId && m.authorId !== me && m.createdAt > (lastRead[id] ?? 0)).length;
  const channels = Object.values(db.channels)
    .filter((c) => c.workspaceId === ws && c.memberIds.includes(me))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const favorites = Object.values(db.favorites)
    .filter((f) => f.userId === me)
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((f) => ({ f, t: favTarget(db, f, ws) }))
    .filter((x) => x.t);
  const spaces = spacesOf(db, ws).filter((s) => !s.archived);

  return (
    <div className="m-page">
      <TopHeader />
      <div className="m-scroll">
        <div className="m-pad">
          <SearchBar />
        </div>
        <div className="m-cards">
          <Card accent icon={<CalendarDays size={20} />} title={`Today ${today}`} sub={overdue ? `${overdue} overdue` : 'Nothing overdue'} onClick={() => navigate(`/${ws}/my-tasks`)} />
          <Card icon={<MessageSquareReply size={20} />} title="Replies" sub={`${replies} new`} onClick={() => navigate(`/${ws}/replies`)} />
          <Card icon={<AtSign size={20} />} title="Assigned" sub={`${assigned} comment${assigned === 1 ? '' : 's'}`} onClick={() => navigate(`/${ws}/assigned-comments`)} />
          <Card icon={<Activity size={20} />} title="Activity" sub={activity ? `${activity} new` : '—'} onClick={() => navigate(`/${ws}/chat/activity`)} />
          <Card icon={<CalendarClock size={20} />} title="Upcoming" sub={`${upcoming} item${upcoming === 1 ? '' : 's'}`} onClick={() => navigate(`/${ws}/my-tasks`)} />
        </div>

        {recentRows.length > 0 && (
          <Section id="recents" title="Recents">
            {recentRows.map((r) => (
              <NavRow key={r.key} icon={r.icon} label={r.name} onClick={r.go} />
            ))}
          </Section>
        )}

        <Section id="channels" title="Channels" onAdd={guest ? undefined : () => navigate(`/${ws}/chat?new=channel`)}>
          {channels
            .filter((c) => c.kind === 'channel')
            .map((c) => (
              <NavRow
                key={c.id}
                icon={<Hash size={20} />}
                label={c.name}
                extra={c.private ? <Lock size={13} className="faint" /> : undefined}
                badge={unreadIn(c.id)}
                onClick={() => navigate(`/${ws}/chat/${c.id}`)}
              />
            ))}
        </Section>

        <Section id="dms" title="DM's" onAdd={() => navigate(`/${ws}/chat?new=dm`)}>
          {channels
            .filter((c) => c.kind === 'dm')
            .map((c) => {
              const other = c.memberIds.find((u) => u !== me) ?? me;
              return <NavRow key={c.id} icon={<Avatar userId={other} size={26} showPresence />} label={channelTitle(db, c, me)} badge={unreadIn(c.id)} onClick={() => navigate(`/${ws}/chat/${c.id}`)} />;
            })}
        </Section>

        {favorites.length > 0 && (
          <Section id="favorites" title="Favorites">
            {favorites.map(({ f, t }) => (
              <NavRow key={f.id} icon={<SpaceIconFor f={f} />} label={t!.name} onClick={() => navigate(t!.path)} />
            ))}
          </Section>
        )}

        <Section id="spaces" title="Spaces">
          {!guest && <NavRow icon={<span className="m-tile-icon"><Layers size={16} /></span>} label={<>All Tasks <span className="faint">– {db.workspaces[ws]?.name}</span></>} onClick={() => navigate(`/${ws}/everything`)} />}
          {spaces.map((s) => (
            <NavRow key={s.id} icon={<SpaceAvatar spaceId={s.id} size={26} />} label={s.name} chevron onClick={() => navigate(`/${ws}/s/${s.id}`)} />
          ))}
          {spaces.length === 0 && <div className="m-empty-line">{guest ? 'Nothing has been shared with you yet.' : 'No spaces yet.'}</div>}
        </Section>
      </div>
    </div>
  );
}

function SpaceIconFor({ f }: { f: Favorite }) {
  if (f.target.type === 'space') return <SpaceAvatar spaceId={f.target.id} size={22} />;
  if (f.target.type === 'channel') return <Hash size={20} />;
  return <Layers size={20} />;
}
